import React from 'react';
import clsx from 'clsx';
import { AImage } from '../../atoms/AImage/index';
import { MPhotoCaption } from '../../molecules/MPhotoCaption/index';
import { AScrimGradient } from '../../atoms/AScrimGradient/index';
import { MLogoContainer } from '../../molecules/MLogoContainer/index';
import { MTextBlockSimple } from '../../molecules/MTextBlockSimple/index';
import { MButton } from '../../molecules/MButton/index';
import { OCardLayeredFeatureProps, OCardLayeredFeatureImageScale } from './OCardLayeredFeature.types';
import * as Styled from './OCardLayeredFeature.styles';
import { AHeaderSize, AHeaderTag } from '../../atoms/AHeader';

const COMPONENT_NAME = 'OCardLayeredFeature';
const TESTID = 'o-card-layered-feature';

export const OCardLayeredFeature: React.FC<OCardLayeredFeatureProps> = ({
  imageScale = OCardLayeredFeatureImageScale.Medium,
  ctaLink,
  aImage,
  mPhotoCaption,
  aScrimGradient,
  mLogoContainer,
  mTextBlockSimple,
  mButton,
  className,
  trackingProperties: _trackingProperties,
  ...rest
}) => {
  const isClickable = Boolean(ctaLink);

  const { aBody = {}, aEyebrow = {}, aHeader = {} } = mTextBlockSimple ?? {};

  const modifiedMTextBlockSimple = {
    bodyText: aBody.bodyText ?? '',
    eyebrowText: aEyebrow.eyebrowText ?? '',
    headerText: aHeader.text ?? '',
    headerSize: aHeader.size as AHeaderSize,
    headerTag: aHeader.headerTag as AHeaderTag,
  };

  const handleCardClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isClickable && ctaLink) {
      if (e.target === e.currentTarget || !(e.target as HTMLElement).closest('a, button')) {
        window.location.href = ctaLink;
      }
    }
  };

  return (
    <Styled.CardWrapper
      className={clsx(className)}
      data-component-name={COMPONENT_NAME}
      data-testid={TESTID}
      $imageScale={imageScale}
      $isClickable={isClickable}
      onClick={handleCardClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      {...rest}
    >
      <Styled.ImageContainer $imageScale={imageScale}>
        {aImage && <AImage {...aImage} loading="lazy" />}
        {aScrimGradient && (
          <Styled.ScrimWrapper>
            <AScrimGradient {...aScrimGradient} />
          </Styled.ScrimWrapper>
        )}
      </Styled.ImageContainer>

      <Styled.ContentOverlay $imageScale={imageScale}>
        {mLogoContainer && (
          <Styled.LogoWrapper>
            <MLogoContainer {...mLogoContainer} />
          </Styled.LogoWrapper>
        )}

        {mTextBlockSimple && (
          <Styled.TextBlockWrapper>
            <MTextBlockSimple {...modifiedMTextBlockSimple} />
          </Styled.TextBlockWrapper>
        )}

        {mButton && (
          <Styled.ButtonWrapper>
            <MButton {...mButton} />
          </Styled.ButtonWrapper>
        )}

        {mPhotoCaption && (
          <Styled.PhotoCaptionWrapper>
            <MPhotoCaption {...mPhotoCaption} />
          </Styled.PhotoCaptionWrapper>
        )}
      </Styled.ContentOverlay>
    </Styled.CardWrapper>
  );
};

export default OCardLayeredFeature;
